import { useCallback, useMemo, useState } from 'react';
import { Switch } from 'antd';
import { useUpdateSchedulingMutation } from '../../queries/scheduling';
import { Field, FieldLabel, FormField, FormModal, FormRow, SearchSelect, Select } from '../../components';
import { BUFFER_OPTIONS, DURATION_OPTIONS, WEEKDAYS, minutesLabel } from './constants';
import { DEFAULT_TZ } from './time';
import { DayHead, DayRow, DayRows, ErrorText, Hint } from './styled';

const DEFAULT_RANGE = { start: '09:00', end: '17:00' };
const TIMEZONES = Intl.supportedValuesOf?.('timeZone') ?? [DEFAULT_TZ];

const changedKeys = (form, initial) =>
  Object.fromEntries(Object.entries(form).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(initial[k])));

const isInt = (v, min, max) => Number.isInteger(v) && v >= min && v <= max;

function validate(form) {
  const badDay = WEEKDAYS.find(({ day }) => (form.weeklyHours[day] || []).some((r) => !r.start || !r.end || r.start >= r.end));
  if (badDay) return `${badDay.label} closes before it opens.`;
  if (!isInt(form.leadTimeHours, 0, 168)) return 'Lead time must be 0 to 168 hours.';
  if (!isInt(form.maxAdvanceDays, 1, 90)) return 'Booking window must be 1 to 90 days.';
  return null;
}

function useRulesForm(rules) {
  const [form, setForm] = useState(rules);
  const set = useCallback((key, value) => setForm((f) => ({ ...f, [key]: value })), []);

  const setDay = useCallback(
    (day, patch) =>
      setForm((f) => {
        const ranges = f.weeklyHours[day] || [];
        const next = patch === null ? [] : [{ ...(ranges[0] || DEFAULT_RANGE), ...patch }, ...ranges.slice(1)];
        return { ...f, weeklyHours: { ...f.weeklyHours, [day]: next } };
      }),
    []
  );

  const error = useMemo(() => validate(form), [form]);
  const changes = useMemo(() => changedKeys(form, rules), [form, rules]);
  return { form, set, setDay, error, changes };
}

function DayHours({ day, label, ranges = [], onChange }) {
  const range = ranges[0];
  const split = ranges.length > 1;
  return (
    <DayRow>
      <label>
        <input type="checkbox" checked={Boolean(range)} disabled={split} onChange={(e) => onChange(day, e.target.checked ? {} : null)} />
        {label}
      </label>
      {split ? (
        <Hint>Split hours. Ask Home Matters to change them.</Hint>
      ) : (
        <>
          <Field
            type="time"
            aria-label={`${label} opens`}
            disabled={!range}
            value={range?.start || ''}
            onChange={(e) => onChange(day, { start: e.target.value })}
          />
          <Field
            type="time"
            aria-label={`${label} closes`}
            disabled={!range}
            value={range?.end || ''}
            onChange={(e) => onChange(day, { end: e.target.value })}
          />
        </>
      )}
    </DayRow>
  );
}

export default function RulesFormModal({ rules, onClose }) {
  const { form, set, setDay, error, changes } = useRulesForm(rules);
  const update = useUpdateSchedulingMutation();
  const timezones = TIMEZONES.includes(form.timezone) ? TIMEZONES : [form.timezone, ...TIMEZONES];

  const save = () => {
    if (Object.keys(changes).length) update.mutate(changes);
    onClose();
  };

  const num = (key) => (e) => set(key, e.target.value === '' ? NaN : Number(e.target.value));

  return (
    <FormModal
      title="Scheduling rules"
      subtitle="When your assistant can book tours and appointments"
      okText="Save rules"
      width={560}
      top={64}
      valid={!error}
      onSubmit={save}
      onClose={onClose}
    >
        <FormField>
          <FieldLabel htmlFor="rules-enabled">Assistant booking</FieldLabel>
          <div>
            <Switch id="rules-enabled" checked={form.enabled} onChange={(v) => set('enabled', v)} />
          </div>
        </FormField>

        <DayRows>
          <legend>Bookable hours</legend>
          <DayHead aria-hidden="true">
            <span>Day</span>
            <span>Opens</span>
            <span>Closes</span>
          </DayHead>
          {WEEKDAYS.map(({ day, label }) => (
            <DayHours key={day} day={day} label={label} ranges={form.weeklyHours[day]} onChange={setDay} />
          ))}
        </DayRows>

        <FormRow>
          <FormField>
            <FieldLabel htmlFor="rules-duration">Tour / appointment duration</FieldLabel>
            <Select id="rules-duration" style={{ minWidth: 0 }} value={form.slotDurationMin} onChange={num('slotDurationMin')}>
              {DURATION_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {minutesLabel(n)}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField>
            <FieldLabel htmlFor="rules-buffer">Buffer between appointments</FieldLabel>
            <Select id="rules-buffer" style={{ minWidth: 0 }} value={form.bufferMin} onChange={num('bufferMin')}>
              {BUFFER_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n ? minutesLabel(n) : 'None'}
                </option>
              ))}
            </Select>
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <FieldLabel htmlFor="rules-lead">Minimum lead time (hours)</FieldLabel>
            <Field id="rules-lead" type="number" min={0} max={168} value={Number.isNaN(form.leadTimeHours) ? '' : form.leadTimeHours} onChange={num('leadTimeHours')} />
          </FormField>
          <FormField>
            <FieldLabel htmlFor="rules-window">Booking window (days)</FieldLabel>
            <Field id="rules-window" type="number" min={1} max={90} value={Number.isNaN(form.maxAdvanceDays) ? '' : form.maxAdvanceDays} onChange={num('maxAdvanceDays')} />
          </FormField>
        </FormRow>

        <FormField>
          <FieldLabel htmlFor="rules-tz">Time zone</FieldLabel>
          <SearchSelect
            id="rules-tz"
            value={form.timezone}
            onChange={(v) => set('timezone', v)}
            options={timezones.map((tz) => ({ value: tz, label: tz.replace(/_/g, ' ') }))}
          />
        </FormField>

        {error && <ErrorText role="alert">{error}</ErrorText>}
    </FormModal>
  );
}
